import { useState } from "react";
import { usePagedDocuments } from "@/hooks/usePagedDocuments";
import type { EmailDocument } from "@/types/email";

interface StepSelectDocumentProps {
  selected: EmailDocument | null;
  onSelect: (doc: EmailDocument) => void;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export function StepSelectDocument({ selected, onSelect }: StepSelectDocumentProps) {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const { documents, totalPages, isLoading, error } = usePagedDocuments(currentPage, 9);

  const filtered = search
    ? documents.filter((d) => d.name.toLowerCase().includes(search.toLowerCase()))
    : documents;

  return (
    <div className="lw-step-select-document">
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-foreground">Select an email</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Choose the email you want to send in this campaign.
        </p>
      </div>

      {/* Search */}
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search emails…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-primary" />
            Loading…
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && filtered.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-12 text-center">
          <p className="text-sm text-muted-foreground">
            {search ? `No emails matching "${search}"` : "No emails yet. Create one first."}
          </p>
        </div>
      )}

      {/* Document grid */}
      {!isLoading && !error && filtered.length > 0 && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((doc) => {
              const isSelected = selected?.id === doc.id;
              return (
                <button
                  key={doc.id}
                  onClick={() => onSelect(doc)}
                  className={`lw-doc-picker-card text-left rounded-lg border transition-all duration-150 p-3 hover:border-primary/60 hover:shadow-sm ${
                    isSelected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "border-border bg-card"
                  }`}
                >
                  {/* Thumbnail sketch */}
                  <div className="mb-2 h-24 overflow-hidden rounded bg-background p-2">
                    <div className="flex flex-col gap-1.5 h-full">
                      <div className="h-2 rounded-sm bg-primary/20 w-full" />
                      <div className="h-1.5 rounded-sm bg-muted-foreground/10 w-3/4" />
                      <div className="h-1 rounded-sm bg-muted-foreground/15 w-full" />
                      <div className="h-1 rounded-sm bg-muted-foreground/15 w-5/6" />
                      <div className="h-3 rounded-sm bg-primary/15 w-1/3 mx-auto mt-1" />
                    </div>
                  </div>
                  <div className="flex items-start justify-between gap-1">
                    <p className="text-xs font-medium text-foreground line-clamp-1 flex-1">{doc.name}</p>
                    {isSelected && (
                      <svg className="h-4 w-4 text-primary shrink-0" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5l-4-4 1.41-1.41L10 13.67l6.59-6.59L18 8.5l-8 8z" />
                      </svg>
                    )}
                  </div>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">Updated {formatDate(doc.updatedAt)}</p>
                </button>
              );
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                ←
              </button>
              <span className="text-xs text-muted-foreground">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
